import { useMemo } from "react";
import { useInput, type InputProps } from "react-admin";
import ReactQuill from "react-quill";
import { Box, Paper, Typography } from "@mui/material";
import "react-quill/dist/quill.snow.css";

const TOOLBAR = [
  [{ header: [2, 3, false] }],
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["blockquote", "link"],
  ["clean"],
];

type RichTextInputProps = InputProps & {
  helperText?: string;
};

/**
 * WYSIWYG HTML editor (Quill). Mobile About screen renders via react-native-render-html.
 */
export const RichTextInput = ({
  label,
  helperText,
  ...props
}: RichTextInputProps) => {
  const { field, fieldState, isRequired } = useInput(props);
  const modules = useMemo(() => ({ toolbar: TOOLBAR }), []);

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, width: "100%" }}>
      {label ? (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
          {isRequired ? " *" : ""}
        </Typography>
      ) : null}
      <Box
        sx={{
          "& .ql-toolbar.ql-snow": {
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            borderColor: "divider",
            bgcolor: "grey.50",
          },
          "& .ql-container.ql-snow": {
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            borderColor: "divider",
            minHeight: 220,
            fontSize: 15,
            fontFamily: "inherit",
          },
          "& .ql-editor": {
            minHeight: 220,
          },
        }}>
        <ReactQuill
          theme="snow"
          defaultValue={typeof field.value === "string" ? field.value : ""}
          onChange={(html, _delta, _source, editor) => {
            const next =
              html === "<p><br></p>" ? "" : editor.getText().trim() ? html : "";
            // Quill emits a synthetic change during mount; defer it so RHF's
            // onChange is never invoked inside the render cycle.
            setTimeout(() => field.onChange(next), 0);
          }}
          onBlur={() => field.onBlur()}
          modules={modules}
        />
      </Box>
      {helperText ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {helperText}
        </Typography>
      ) : null}
      {fieldState.error ? (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          {fieldState.error.message}
        </Typography>
      ) : null}
    </Paper>
  );
};
